import React, { useEffect, useState } from "react";
import { DataGridPro, GridToolbar } from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import {
  Box,
  Button,
  Switch,
  FormControlLabel,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import AddUserModal from "./Modals/AddUserModal";
import EditUserModal from "./Modals/EditUserModal";
import CustomAlert from "../../Common/Components/CustomAlert";

function InternalUserManagementView() {
  const [users, setUsers] = useState([]);
  const [rows, setRows] = useState([]);
  const [showInactive, setShowInactive] = useState(false);
  const [alert, setAlert] = useState({ open: false, type: "success", message: "" });
  const [openAddUserModal, setOpenAddUserModal] = useState(false);
  const [openEditUserModal, setOpenEditUserModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [departments, setDepartments] = useState([]);

  const userState = useSelector((state) => state.user);

  // -----------------------------
  // DataGrid Columns
  // -----------------------------
  const columns = [
    { field: "id", headerName: "ID", width: 90 },
    { field: "username", headerName: "Username", width: 150 },
    { field: "firstname", headerName: "First name", width: 150 },
    { field: "lastName", headerName: "Lastname", width: 150 },
    { field: "email", headerName: "Email", width: 250 },
    { field: "noTelephone", headerName: "No Telephone", width: 150 },
    { field: "lang", headerName: "Lang", width: 120 },
    { field: "departmentDisplay", headerName: "Department", width: 180 },
    { field: "title", headerName: "Title", width: 150 },
    {
      field: "actions",
      headerName: "Actions",
      width: 100,
      sortable: false,
      filterable: false,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <Tooltip title="Edit">
          <IconButton
            size="small"
            color="primary"
            onClick={() => handleOpenEditUserModal(params.row)}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      ),
    },
  ];

  // -----------------------------
  // Fetch Users
  // -----------------------------
  const fetchUsers = async () => {
    if (!userState?.token) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Employee/GetInternalEmployees`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      setUsers(data || []);
    } catch (err) {
      console.error(err);
      setUsers([]);
    }
  };

  // -----------------------------
  // Fetch Departments
  // -----------------------------
  const fetchDepartments = async () => {
    if (!userState?.token) return;
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/Localization/GetInternalDepartment`,
        {
          method: "GET",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${userState.token}`,
          },
        }
      );
      if (!res.ok) throw new Error(`Erreur ${res.status}`);
      const data = await res.json();
      setDepartments(data || []);
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchUsers();
    fetchDepartments();
  }, [userState.token]);

  // -----------------------------
  // Build rows for DataGrid
  // -----------------------------
  useEffect(() => {
    const filtered = users
      .filter((u) => showInactive || !u.estInactif)
      .map((e) => ({
        id: e.noEmploye,
        noEmploye: e.noEmploye,
        firstname: e.firstname,
        lastName: e.lastName,
        username: e.username,
        email: e.email,
        noTelephone: e.noCellulaire,
        lang: e.language,
        department: e.department,
        departmentDisplay: e.department?.textFr || e.department?.textEn || "",
        title: e.title,
        estInactif: e.estInactif,
      }));

    setRows(filtered);
  }, [users, showInactive]);

  // -----------------------------
  // Alerts
  // -----------------------------
  const showAlert = (type, message) =>
    setAlert({ open: true, type, message });
  const handleCloseAlert = () =>
    setAlert((prev) => ({ ...prev, open: false }));

  // -----------------------------
  // Modals
  // -----------------------------
  const handleOpenAddUserModal = () => setOpenAddUserModal(true);
  const handleCloseAddUserModal = () => setOpenAddUserModal(false);

  const handleOpenEditUserModal = (user) => {
    setSelectedUser(user);
    setOpenEditUserModal(true);
  };
  const handleCloseEditUserModal = () => {
    setSelectedUser(null);
    setOpenEditUserModal(false);
  };

  // -----------------------------
  // Add User
  // -----------------------------
  const handleAddUser = async (newUser) => {
    const requiredFields = ["username", "firstname", "lastName", "email", "department"];
    for (const f of requiredFields) {
      if (!newUser[f]) {
        showAlert("error", "Veuillez remplir tous les champs obligatoires !");
        return;
      }
    }

    try {
      // Si tu avais un POST vers le backend, tu le ferais ici
      // Exemple :
      // await fetch(`${process.env.REACT_APP_API_URL}/Employee/Add`, {...})

      // Pour l'instant on simule un ajout local
      const userToAdd = {
        noEmploye: Date.now(),
        username: newUser.username,
        firstname: newUser.firstname,
        lastName: newUser.lastName,
        email: newUser.email,
        noCellulaire: newUser.noTelephone,
        language: newUser.lang,
        department: newUser.department,
        title: newUser.title,
        estInactif: false,
      };

      // Ajout local temporaire (facultatif)
      setUsers((prev) => [userToAdd, ...prev]);

      // Rappel de fetchUsers pour recharger depuis le backend
      await fetchUsers();

      showAlert("success", "Utilisateur ajouté avec succès !");
      handleCloseAddUserModal();
    } catch (err) {
      console.error(err);
      showAlert("error", "Erreur lors de l'ajout de l'utilisateur !");
    }
  };

  // -----------------------------
  // Edit User
  // -----------------------------
  const handleEditUser = async (updatedUser) => {
    try {
      // Si tu avais un PUT/PATCH vers le backend, tu le ferais ici
      // Exemple :
      // await fetch(`${process.env.REACT_APP_API_URL}/Employee/Update/${updatedUser.noEmploye}`, {...})

      // Mise à jour locale (facultative)
      setUsers((prev) =>
        prev.map((u) => (u.noEmploye === updatedUser.noEmploye ? updatedUser : u))
      );

      // Rappel de fetchUsers pour recharger depuis le backend
      await fetchUsers();

      showAlert("success", "Utilisateur modifié avec succès !");
      handleCloseEditUserModal();
    } catch (err) {
      console.error(err);
      showAlert("error", "Erreur lors de la modification de l'utilisateur !");
    }
  };

  return (
    <Box sx={{ width: "95%", mx: "auto", mt: 4 }}>
      <Paper
        elevation={3}
        sx={{
          p: 2,
          mb: 3,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button variant="contained" onClick={handleOpenAddUserModal}>
          Add User
        </Button>
        <FormControlLabel
          control={
            <Switch
              checked={showInactive}
              onChange={(e) => setShowInactive(e.target.checked)}
            />
          }
          label="Show Inactive Employees"
        />
      </Paper>

      <AddUserModal
        open={openAddUserModal}
        onClose={handleCloseAddUserModal}
        onAddUser={handleAddUser}
        departments={departments}
        userLang={userState.lang}
        userState={userState}
      />

      <EditUserModal
        open={openEditUserModal}
        onClose={handleCloseEditUserModal}
        user={selectedUser}
        departments={departments}
        userLang={userState.lang}
        onSave={handleEditUser}
        userState={userState}
      />

      <Paper elevation={3} sx={{ height: 700, width: "100%", p: 2 }}>
        <DataGridPro
          rows={rows}
          columns={columns}
          slots={{ toolbar: GridToolbar }}
          slotProps={{
            toolbar: {
              showQuickFilter: true,
              quickFilterProps: { debounceMs: 500 },
            },
          }}
          pageSizeOptions={[5, 10, 50]}
        />
      </Paper>

      <CustomAlert
        open={alert.open}
        typeMessage={alert.type}
        message={alert.message}
        closeMessage={handleCloseAlert}
      />
    </Box>
  );
}

export default InternalUserManagementView;
